import type { ReactNode } from "react";

import type { ButtonVariant, ButtonTheme } from "@/components/Button";
import type { MaterialNames } from "@/components/material_symbol_names";
import type { MenuItem, MenuListEntry } from "@/components/Menu";

export type ActionVariant = Extract<ButtonVariant, "filled" | "outlined" | "text">;

export type ActionSection = "Manage" | "Actions" | "Sharing" | "Admin/Dev Only" | "Contact";

/**
 * A single action in a header-style action cluster. Callers pass an ordered
 * array of these — the first eligible (non-`menuOnly`) action in array order is
 * the inline primary when consolidation applies. Subsequent eligible actions
 * render inline up to `maxInline` (default two). With three or more actions
 * (including `menuOnly`), the first eligible stays inline and the rest collapse
 * into a {@link MenuButton}. A pair is never split into one inline button plus a
 * one-item menu — when `maxInline` is 1, both go in the menu. Actions marked
 * `menuOnly` never render inline, but they still count toward the three-action
 * overflow threshold.
 */
export interface Action {
  text: string;
  /** Leading icon. Rendered next to the label both inline and inside the menu. */
  iconName?: MaterialNames;
  onClick?: () => void;
  testId?: string;
  /** Inline render only. Defaults to `filled`. Ignored inside the MenuButton menu. */
  variant?: ActionVariant;
  /** Inline render only. Forwarded to Button (e.g. `error` for a red secondary action). Ignored in the menu. */
  theme?: ButtonTheme;
  /** Inline only. Forwarded to Button `iconClassName` (e.g. a saving spinner). */
  iconClassName?: string;
  /** When true, the inline Button and overflow menu row are not clickable. */
  disabled?: boolean;
  /** When true, the action only appears inside the overflow menu — never as an inline Button. It still counts toward the three-action overflow threshold. */
  menuOnly?: boolean;
  /** Menu only. Forces a divider above this row. Destructive actions get one automatically. */
  dividerAbove?: boolean;
  /** Menu only. Groups the action under a section heading. Section transitions emit a new heading. */
  section?: ActionSection;
  /** Menu only. Renders red, sorts to the bottom, and gets an automatic divider above. */
  destructive?: boolean;
  /** Menu only. Overrides `text` so the row can render rich content (e.g. bolded status word). */
  menuLabel?: ReactNode;
  /** Menu only. Secondary line below the label (e.g. phone number or email). */
  supportingText?: string;
  /** Menu only. When true, supporting text may wrap; defaults to menu row behavior when omitted. */
  supportingTextWrap?: boolean;
  /** Menu only. Shows a copy control for `copyText` (defaults to `supportingText`). */
  copyable?: boolean;
  /** Menu only. Value copied when the row copy control is used. */
  copyText?: string;
  /** Menu only. Analytics-safe field name for the trailing copy control. */
  copyFieldLabel?: string;
  /** Menu only. Internal system identifier for Amplitude debug attributes on allowlisted ID fields. */
  copyFieldId?: string;
  /** Menu only. Snackbar text for the trailing copy control when feedback is snackbar. */
  copySuccessMessage?: string;
  /** Overrides the default accessible name for this action (menu row or inline button). */
  ariaLabel?: string;
  /** Inline only. Renders a native CopyButton instead of Material Web Button for reliable analytics attribution. */
  copyButton?: {
    textToCopy: string;
    ariaLabel?: string;
    copyFieldLabel?: string;
    copyFieldId?: string;
    feedback?: "tooltip" | "snackbar" | "none";
    successMessage?: string;
    onCopied?: () => void;
  };
  /** Menu only. Overrides the default menu row class. */
  itemClassName?: string;
}

/** Default test id shared across header surfaces so tests don't care which one they're on. */
export const DEFAULT_MENU_BUTTON_TEST_ID = "extended-actions-menu-button";

/** Eligible actions that stay inline when the row has room. */
export const DEFAULT_MAX_INLINE_ACTIONS = 2;

/** Overflow trigger when leftover actions sit beside an inline button. */
export const OVERFLOW_MENU_LABEL = "More";

/** Focused-page compact trigger when every action lives in the menu. ActionCluster still defaults to {@link OVERFLOW_MENU_LABEL}. */
export const MENU_ONLY_CLUSTER_LABEL = "Actions";

export interface ConsolidateActionsInput {
  actions?: Action[];
  menuButtonTestId?: string;
  /**
   * Max eligible actions to keep inline when there are fewer than three total
   * actions. Defaults to {@link DEFAULT_MAX_INLINE_ACTIONS}. Three or more
   * actions (including `menuOnly`) still keep only the first eligible inline. A
   * pair is never split into a one-item menu: `maxInline: 1` puts both in the
   * menu.
   */
  maxInline?: number;
}

export interface ConsolidationResult {
  /** Actions rendered as inline Buttons (never includes `menuOnly` actions). */
  inlineActions: Action[];
  /** Overflow and `menuOnly` actions rendered inside a MenuButton. */
  menuItems: MenuListEntry[];
  menuButtonTestId: string;
}

/** @deprecated Use {@link ConsolidationResult} — inline-only results use `menuItems: []`. */
export type InlineConsolidationResult = ConsolidationResult;

/** @deprecated Use {@link ConsolidationResult} — menu results set `inlineActions` to the primary only. */
export type MenuConsolidationResult = ConsolidationResult & { inlineActions: [Action] };

export function consolidateActions({
  actions,
  menuButtonTestId = DEFAULT_MENU_BUTTON_TEST_ID,
  maxInline = DEFAULT_MAX_INLINE_ACTIONS
}: ConsolidateActionsInput): ConsolidationResult {
  const list = actions ?? [];
  const menuOnly = list.filter((action) => action.menuOnly);
  const eligible = list.filter((action) => !action.menuOnly);

  let inlineActions: Action[] = [];
  let overflowEligible: Action[] = [];

  if (list.length >= 3) {
    inlineActions = eligible.slice(0, 1);
    overflowEligible = eligible.slice(1);
  } else if (eligible.length > maxInline && eligible.length >= 2) {
    inlineActions = [];
    overflowEligible = eligible;
  } else {
    inlineActions = eligible;
  }

  const menuActions = [...overflowEligible, ...menuOnly];
  const menuItems = menuActions.length > 0 ? toMenuListEntries(menuActions) : [];

  return {
    inlineActions,
    menuItems,
    menuButtonTestId
  };
}

export function toMenuListEntries(actions: Action[]): MenuListEntry[] {
  const benign = actions.filter((action) => !action.destructive);
  const destructive = actions.filter((action) => action.destructive);
  const ordered = [...benign, ...destructive];

  const entries: MenuListEntry[] = [];
  let currentSection: ActionSection | undefined = undefined;
  let lastItem: MenuItem | undefined;
  let lastWasDestructive = false;

  ordered.forEach((action) => {
    if (!action.destructive && action.section && action.section !== currentSection) {
      entries.push({ type: "headline", label: action.section, useDivider: false });
      currentSection = action.section;
    }

    // `MenuItem.useDivider` paints the divider *below* the flagged row, so
    // "divider above this row" becomes `useDivider: true` on the previous item.
    const wantsDividerAbove = action.dividerAbove === true || (action.destructive && !lastWasDestructive);
    if (wantsDividerAbove && lastItem) lastItem.useDivider = true;

    const item = toMenuItem(action);
    entries.push(item);
    lastItem = item;
    lastWasDestructive = action.destructive === true;
  });

  return entries;
}

function toMenuItem(action: Action): MenuItem {
  const nestedCopy = action.copyButton;
  const copyFieldLabel = action.copyFieldLabel ?? nestedCopy?.copyFieldLabel;
  const copyFieldId = action.copyFieldId ?? nestedCopy?.copyFieldId;
  const ariaLabel = action.ariaLabel ?? nestedCopy?.ariaLabel;

  // When a `copyButton` action overflows, promote nested copy props and clipboard
  // work onto the whole-row menu item so consolidation cannot silently drop them.
  const onClick =
    action.onClick ??
    (nestedCopy
      ? () => {
          void navigator.clipboard.writeText(nestedCopy.textToCopy).then(() => {
            nestedCopy.onCopied?.();
          });
        }
      : undefined);

  const item: MenuItem = {
    label: action.menuLabel ?? action.text,
    onClick,
    dataTestId: action.testId,
    useDivider: false,
    itemClassName: action.itemClassName ?? "whitespace-nowrap"
  };
  if (action.iconName) item.iconName = action.iconName;
  if (action.disabled) item.disabled = true;
  if (action.destructive) item.destructive = true;
  if (action.supportingText) item.supportingText = action.supportingText;
  if (action.supportingTextWrap !== undefined) item.supportingTextWrap = action.supportingTextWrap;
  if (action.copyable) item.copyable = true;
  if (action.copyText) item.copyText = action.copyText;
  if (copyFieldLabel) item.copyFieldLabel = copyFieldLabel;
  if (copyFieldId) item.copyFieldId = copyFieldId;
  if (action.copySuccessMessage) item.copySuccessMessage = action.copySuccessMessage;
  if (ariaLabel) item.ariaLabel = ariaLabel;
  return item;
}
